import { create } from 'zustand';

type Urgency = 'polite' | 'assertive';

/**
 * Screen-reader announcements for the whole app.
 *
 * The running app renders exactly TWO live regions (see <LiveRegions />), and everything that
 * needs to be spoken goes through this store. That constraint is the point: several live
 * regions on one screen is the classic reason a screen reader reads announcements interleaved
 * and half-swallowed — the accessibility audit found four of them competing on the game board,
 * so "your turn" could land in the middle of the scene text.
 *
 * polite    — narrative, status, anything that can wait its turn.
 * assertive — interrupts whatever is being read: your turn, errors, lost connection. Rare.
 */
interface A11yState {
  polite: string;
  assertive: string;
  announce: (message: string, urgency?: Urgency) => void;
  clear: () => void;
}

export const useA11yStore = create<A11yState>((set) => ({
  polite: '',
  assertive: '',

  announce: (message, urgency = 'polite') =>
    set((state) => {
      if (!message) return {};
      // Re-announcing the same string is a real case ("Your turn." two rounds running). An
      // unchanged text node is not a DOM mutation, so the screen reader stays silent. A
      // trailing zero-width space makes the value new without changing a spoken character.
      const ZWSP = '\u200B';
      const bare = message.replace(/\u200B+$/, '');
      const current = state[urgency].replace(/\u200B+$/, '');
      const next = current === bare ? bare + ZWSP : bare;
      return urgency === 'assertive' ? { assertive: next } : { polite: next };
    }),

  clear: () => set({ polite: '', assertive: '' }),
}));
